import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import apiUtils from '../../global/utils/api';

import {
  handleCreateFulfilled
  , handleFetchSinglePending
  , handleFetchSingleFulfilled
  , handleFetchSingleFromListFulfilled
  , handleFetchSingleRejected
  , handleFetchListPending
  , handleFetchListFulfilled
  , handleFetchListRejected
  , handleMutationPending
  , handleMutationFulfilled
  , handleMutationRejected
  , handleDeletePending
  , handleDeleteFulfilled
  , handleDeleteRejected
  , shouldFetch
  , INITIAL_STATE
  , handleInvalidateQuery
  , handleInvalidateQueries
  , handleAddSingleToList
} from '../../global/utils/storeUtils';


// First define all API calls for groupRole
/**
 * The functions below, called thunks, allow us to perform async logic. They
 * can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
 * will call the thunk with the `dispatch` function as the first argument. Async
 * code can then be executed and other actions can be dispatched. Thunks are
 * typically used to make async requests.
 * 
 * In practice we won't dispatch these directly, they will be dispatched by groupRoleService which has a nicer api built on hooks.
 */

export const myGroupRolesEndpoint = 'logged-in';

// CREATE
export const sendCreateGroupRole = createAsyncThunk(
  'groupRole/sendCreate'
  , async (newGroupRole) => {
    const endpoint = `/api/group-roles`;
    const response = await apiUtils.callAPI(endpoint, 'POST', newGroupRole);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// READ
export const fetchSingleGroupRole = createAsyncThunk(
  'groupRole/fetchSingle'
  , async (id) => {
    const endpoint = `/api/group-roles/${id}`;
    const response = await apiUtils.callAPI(endpoint);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);
export const fetchGroupRoleList = createAsyncThunk(
  'groupRole/fetchList' // this is the action name that will show up in the console logger.
  , async (listArgs) => {
    const endpoint = `/api/group-roles${listArgs}`;
    const response = await apiUtils.callAPI(endpoint);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);


// for each resource we can add as many endpoints as we want in this format and we only need two actions to handle them.
export const fetchSingleGroupRoleAtEndpoint = createAsyncThunk(
  'groupRole/fetchSingleWithFilter' // this is the action name that will show up in the console logger.
  , async (query) => {
    const endpoint = `/api/group-roles${query}` // example: `/api/group-roles/logged-in?${queryString}`
    const response = await apiUtils.callAPI(endpoint);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);
export const fetchGroupRoleListAtEndpoint = createAsyncThunk(
  'groupRole/fetchListWithFilter' // this is the action name that will show up in the console logger.
  , async (query) => {
    const endpoint = `/api/group-roles${query}`; // example: `/api/group-roles/logged-in?${queryString}`
    const response = await apiUtils.callAPI(endpoint);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// UPDATE
export const sendUpdateGroupRole = createAsyncThunk(
  'groupRole/sendUpdate'
  , async ({ _id, ...updates }) => {
    const endpoint = `/api/group-roles/${_id}`;
    const response = await apiUtils.callAPI(endpoint, 'PUT', updates);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// DELETE
export const sendDeleteGroupRole = createAsyncThunk(
  'groupRole/sendDelete'
  , async (id) => {
    const endpoint = `/api/group-roles/${id}`;
    const response = await apiUtils.callAPI(endpoint, 'DELETE');
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// next define the store's initial state, all of our store utils rely on a specific state shape, so use the constant
const initialState = { ...INITIAL_STATE };

// define the groupRoleSlice. This is a combination of actions and reducers. More info: https://redux-toolkit.js.org/api/createSlice
export const groupRoleSlice = createSlice({
  name: 'groupRole'
  , initialState
  /**
   * The `reducers` field lets us define reducers and generate associated actions.
   * Unlike the selectors defined at the bottom of this file, reducers only have access
   * to this specific reducer and not the entire store.
   * 
   * Again, we will not dispatch these directly, they will be dispatched by groupRoleService.
   */
  , reducers: {
    invalidateQuery: handleInvalidateQuery
    , invalidateQueries: handleInvalidateQueries
    , addGroupRoleToList: handleAddSingleToList
  }

  /**
   * The `extraReducers` field lets the slice handle actions defined elsewhere,
   * including actions generated by createAsyncThunk or in other slices.
   * We'll use them to track our server request status.
   * 
   * We'll add a case for each API call defined at the top of the file to dictate
   * what happens during each API call lifecycle.
   */
  , extraReducers: (builder) => {
    builder
      // CREATE
      .addCase(sendCreateGroupRole.fulfilled, handleCreateFulfilled)

      // READ
      .addCase(fetchSingleGroupRole.pending, handleFetchSinglePending)
      .addCase(fetchSingleGroupRole.fulfilled, handleFetchSingleFulfilled)
      .addCase(fetchSingleGroupRole.rejected, handleFetchSingleRejected)
      .addCase(fetchGroupRoleList.pending, handleFetchListPending)
      // because lists are returned from the server named for their resource, we need to pass a `listKey` so the util can properly handle the response
      .addCase(fetchGroupRoleList.fulfilled, (state, action) => handleFetchListFulfilled(state, action, 'groupRoles'))
      .addCase(fetchGroupRoleList.rejected, handleFetchListRejected)

      // permission protected single fetches
      .addCase(fetchSingleGroupRoleAtEndpoint.pending, handleFetchSinglePending)
      // these endpoints return named lists, we need to pass a `listKey` so the util can properly handle the response
      .addCase(fetchSingleGroupRoleAtEndpoint.fulfilled, (state, action) => handleFetchSingleFromListFulfilled(state, action, 'groupRoles'))
      .addCase(fetchSingleGroupRoleAtEndpoint.rejected, handleFetchSingleRejected)
      // permission protected list fetches
      .addCase(fetchGroupRoleListAtEndpoint.pending, handleFetchListPending)
      .addCase(fetchGroupRoleListAtEndpoint.fulfilled, (state, action) => handleFetchListFulfilled(state, action, 'groupRoles'))
      .addCase(fetchGroupRoleListAtEndpoint.rejected, handleFetchListRejected)

      // UPDATE
      .addCase(sendUpdateGroupRole.pending, handleMutationPending)
      .addCase(sendUpdateGroupRole.fulfilled, handleMutationFulfilled)
      .addCase(sendUpdateGroupRole.rejected, handleMutationRejected)
      // .addCase(sendUpdateGroupRole.fulfilled, (state, action) => handleMutationFulfilled(state, action, (newState, action) => {
      //   // by passing this optional callback we now have access to the new state if we want to do something else with it, this works for all reducer handlers
      // }))

      // DELETE
      .addCase(sendDeleteGroupRole.pending, handleDeletePending)
      .addCase(sendDeleteGroupRole.fulfilled, handleDeleteFulfilled)
      .addCase(sendDeleteGroupRole.rejected, handleDeleteRejected)
  }
});

// export the actions for the reducers defined above
export const { invalidateQuery, invalidateQueries, addGroupRoleToList } = groupRoleSlice.actions;


// We can also write thunks by hand, which may contain both sync and async logic.
// Here's an example of conditionally dispatching actions based on current state.
// updated to accept a listFetch function so we can use it for other list fetches (like public landing page groups)
export const fetchListIfNeeded = (queryKey, listFetch = fetchGroupRoleList) => (dispatch, getState) => {
  const groupRoleQuery = getState().groupRole.listQueries[queryKey];
  if(shouldFetch(groupRoleQuery)) {
    // console.log('Fetching group list', queryKey);
    dispatch(listFetch(queryKey));
  } else {
    // console.log('No need to fetch, fresh query in cache');
  }
};

export const fetchSingleIfNeeded = (id, singleFetch = fetchSingleGroupRole) => (dispatch, getState) => {
  const groupRoleQuery = getState().groupRole.singleQueries[id];
  if(shouldFetch(groupRoleQuery)) {
    dispatch(singleFetch(id));
  } else {
    // console.log('No need to fetch, fresh query in cache');
  }
}

export default groupRoleSlice.reducer;
